import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Drawer, TextField, Typography } from '@mui/material';

import PrimaryBlueButton from '../../../components/common/buttons/PrimaryBlueButton';
import BorderedButton from '../../../components/common/buttons/BorderedButton';
import { validateZipcode } from '../../../utils/validations';
import { AccountType } from '../../../types/account';

export type AccountDrawerMode = 'create' | 'edit';

export type AccountDrawerPayload = {
  name: string;
  path_name: string;

  description?: string | null;

  contact_1_name?: string | null;
  contact_1_email?: string | null;
  contact_1_phone?: string | null;

  contact_2_name?: string | null;
  contact_2_email?: string | null;
  contact_2_phone?: string | null;

  address_line_1?: string | null;
  address_line_2?: string | null;
  address_city?: string | null;
  address_state?: string | null;
  address_zip?: string | null;
};

type Props = {
  open: boolean;
  mode: AccountDrawerMode;
  initialAccount?: AccountType | null;
  onClose: () => void;
  onSubmit: (payload: AccountDrawerPayload) => Promise<void> | void;
  isSubmitting?: boolean;
};

const toStrOrEmpty = (v: unknown) => (typeof v === 'string' ? v : '');
const toNullable = (v: string) => (v.trim().length ? v.trim() : null);

const normalizePathName = (s: string) =>
  s
    .trim()
    .toLowerCase()
    .replace(/[^a-z]+/g, ''); // keep only lowercase a-z

type FieldErrors = Partial<
  Record<
    'name' | 'path_name' | 'contact_1_email' | 'address_line_1' | 'address_city' | 'address_state' | 'address_zip',
    string
  >
>;

export default function AccountDrawer({
  open,
  mode,
  initialAccount,
  onClose,
  onSubmit,
  isSubmitting = false,
}: Props): JSX.Element {
  const [errorText, setErrorText] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const [name, setName] = useState('');
  const [pathName, setPathName] = useState('');

  const [description, setDescription] = useState('');

  const [c1Name, setC1Name] = useState('');
  const [c1Email, setC1Email] = useState('');
  const [c1Phone, setC1Phone] = useState('');

  const [c2Name, setC2Name] = useState('');
  const [c2Email, setC2Email] = useState('');
  const [c2Phone, setC2Phone] = useState('');

  const [addr1, setAddr1] = useState('');
  const [addr2, setAddr2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');

  const title = useMemo(() => (mode === 'create' ? 'Add Account' : 'Edit Account'), [mode]);

  useEffect(() => {
    if (!open) return;

    setErrorText(null);
    setFieldErrors({});

    if (mode === 'edit' && initialAccount) {
      setName(toStrOrEmpty(initialAccount.name));
      setPathName(normalizePathName(toStrOrEmpty(initialAccount.path_name)));

      setDescription(toStrOrEmpty(initialAccount.description));

      setC1Name(toStrOrEmpty(initialAccount.contact_1_name));
      setC1Email(toStrOrEmpty(initialAccount.contact_1_email));
      setC1Phone(toStrOrEmpty(initialAccount.contact_1_phone));

      setC2Name(toStrOrEmpty(initialAccount.contact_2_name));
      setC2Email(toStrOrEmpty(initialAccount.contact_2_email));
      setC2Phone(toStrOrEmpty(initialAccount.contact_2_phone));

      setAddr1(toStrOrEmpty(initialAccount.address_line_1));
      setAddr2(toStrOrEmpty(initialAccount.address_line_2));
      setCity(toStrOrEmpty(initialAccount.address_city));
      setState(toStrOrEmpty(initialAccount.address_state));
      setZip(toStrOrEmpty(initialAccount.address_zip));
      return;
    }

    // create defaults
    setName('');
    setPathName('');

    setDescription('');

    setC1Name('');
    setC1Email('');
    setC1Phone('');

    setC2Name('');
    setC2Email('');
    setC2Phone('');

    setAddr1('');
    setAddr2('');
    setCity('');
    setState('');
    setZip('');
  }, [initialAccount, mode, open]);

  const validate = (): { alert?: string; errors: FieldErrors } => {
    const errors: FieldErrors = {};

    if (!name.trim()) errors.name = 'Account name is required.';

    if (!pathName.trim()) errors.path_name = 'Path name is required.';
    else if (!/^[a-z]+$/.test(pathName.trim()))
      errors.path_name = 'Path name must contain only lowercase letters (a-z).';

    if (!addr1.trim()) errors.address_line_1 = 'Address line 1 is required.';
    if (!city.trim()) errors.address_city = 'City is required.';
    if (!state.trim()) errors.address_state = 'State is required.';
    if (!c1Email.trim()) errors.contact_1_email = 'Contact 1 email is required.';

    const z = zip.trim();
    if (!z) errors.address_zip = 'ZIP / Postal code is required.';
    else {
      const zipErr = validateZipcode(z);
      if (zipErr) errors.address_zip = zipErr;
    }

    const alert = Object.keys(errors).length ? 'Please fix the highlighted fields and try again.' : undefined;
    return { alert, errors };
  };

  const handleSubmit = async () => {
    setErrorText(null);

    const { alert, errors } = validate();
    setFieldErrors(errors);

    if (alert) {
      setErrorText(alert);
      return;
    }

    const payload: AccountDrawerPayload = {
      name: name.trim(),
      path_name: normalizePathName(pathName),

      description: toNullable(description),

      contact_1_name: toNullable(c1Name),
      contact_1_email: toNullable(c1Email),
      contact_1_phone: toNullable(c1Phone),

      contact_2_name: toNullable(c2Name),
      contact_2_email: toNullable(c2Email),
      contact_2_phone: toNullable(c2Phone),

      address_line_1: toNullable(addr1),
      address_line_2: toNullable(addr2),
      address_city: toNullable(city),
      address_state: toNullable(state),
      address_zip: toNullable(zip),
    };

    try {
      await onSubmit(payload);
    } catch {
      setErrorText('Unable to save account. Please try again.');
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: '100vw', sm: 520 }, p: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          {title}
        </Typography>

        {errorText ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorText(null)}>
            {errorText}
          </Alert>
        ) : null}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Account Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (fieldErrors.name) setFieldErrors((p) => ({ ...p, name: undefined }));
            }}
            required
            fullWidth
            error={Boolean(fieldErrors.name)}
            helperText={fieldErrors.name || ' '}
          />

          <TextField
            label="Path Name"
            value={pathName}
            onChange={(e) => {
              setPathName(normalizePathName(e.target.value));
              if (fieldErrors.path_name) setFieldErrors((p) => ({ ...p, path_name: undefined }));
            }}
            helperText={fieldErrors.path_name || 'Lowercase letters only (a–z)'}
            required
            fullWidth
            error={Boolean(fieldErrors.path_name)}
          />

          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1 }}>
            Primary Contact
          </Typography>

          <TextField label="Contact 1 Name" value={c1Name} onChange={(e) => setC1Name(e.target.value)} fullWidth />
          <TextField
            label="Contact 1 Email"
            value={c1Email}
            onChange={(e) => {
              setC1Email(e.target.value);
              if (fieldErrors.contact_1_email) setFieldErrors((p) => ({ ...p, contact_1_email: undefined }));
            }}
            required
            fullWidth
            error={Boolean(fieldErrors.contact_1_email)}
            helperText={fieldErrors.contact_1_email || ' '}
          />
          <TextField label="Contact 1 Phone" value={c1Phone} onChange={(e) => setC1Phone(e.target.value)} fullWidth />

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1 }}>
            Secondary Contact
          </Typography>

          <TextField label="Contact 2 Name" value={c2Name} onChange={(e) => setC2Name(e.target.value)} fullWidth />
          <TextField label="Contact 2 Email" value={c2Email} onChange={(e) => setC2Email(e.target.value)} fullWidth />
          <TextField label="Contact 2 Phone" value={c2Phone} onChange={(e) => setC2Phone(e.target.value)} fullWidth />

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1 }}>
            Address
          </Typography>

          <TextField
            label="Address Line 1"
            value={addr1}
            onChange={(e) => {
              setAddr1(e.target.value);
              if (fieldErrors.address_line_1) setFieldErrors((p) => ({ ...p, address_line_1: undefined }));
            }}
            required
            fullWidth
            error={Boolean(fieldErrors.address_line_1)}
            helperText={fieldErrors.address_line_1 || ' '}
          />

          <TextField label="Address Line 2" value={addr2} onChange={(e) => setAddr2(e.target.value)} fullWidth />
          <TextField
            label="City"
            value={city}
            onChange={(e) => {
              setCity(e.target.value);
              if (fieldErrors.address_city) setFieldErrors((p) => ({ ...p, address_city: undefined }));
            }}
            required
            fullWidth
            error={Boolean(fieldErrors.address_city)}
            helperText={fieldErrors.address_city || ' '}
          />

          <TextField
            label="State"
            value={state}
            onChange={(e) => {
              setState(e.target.value);
              if (fieldErrors.address_state) setFieldErrors((p) => ({ ...p, address_state: undefined }));
            }}
            required
            fullWidth
            error={Boolean(fieldErrors.address_state)}
            helperText={fieldErrors.address_state || ' '}
          />

          <TextField
            label="ZIP / Postal"
            value={zip}
            onChange={(e) => {
              setZip(e.target.value);
              if (fieldErrors.address_zip) setFieldErrors((p) => ({ ...p, address_zip: undefined }));
            }}
            required
            fullWidth
            error={Boolean(fieldErrors.address_zip)}
            helperText={fieldErrors.address_zip || ' '}
          />

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <BorderedButton
              onClick={onClose}
              buttonText="Cancel"
              sxProps={{ width: '50%', height: 40 }}
              additionalProps={{ disabled: isSubmitting }}
            />
            <PrimaryBlueButton
              onClick={handleSubmit}
              buttonText={isSubmitting ? 'Saving...' : 'Save'}
              sxProps={{ width: '50%', height: 40 }}
              additionalProps={{ disabled: isSubmitting }}
            />
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}
